import { DataType, FeatureFlag } from '@revelio/core';

import { useUnleashFlag } from '../../../shared/unleash-flags/use-unleash-flag';
import type { DataProviderSet } from '../components/DataProvider/types';
import { useTabContentConfig } from '../components/PageConfigProvider/PageConfigProvider';
import type { ChartType } from '../components/PageConfigProvider/types/chart';
import type { PositionedItem } from '../components/PageConfigProvider/types/item';
import { usePrimaryFilterState } from '../filters/hooks/usePrimaryFilterState';

export const ADAPTIVE_MAP: Partial<Record<ChartType, ChartType>> = {
  bar: 'metric',
  'stacked-bar': 'treemap',
};

type UseAdaptiveChartTypeResult = {
  chartType: ChartType | undefined;
  activePrimaryFilterLength: number;
  isAdaptiveChartMode: boolean;
};

/**
 * When item.plot.adaptive is true, the feature flag is on, and exactly one
 * entity is selected in the primary filter, returns the chart type to use
 * for single-entity view (e.g. bar → metric, stacked-bar → treemap). Otherwise
 * returns the original chartType.
 */
export const useAdaptiveChartType = (
  item: PositionedItem | null
): UseAdaptiveChartTypeResult => {
  const { primaryFilter } = useTabContentConfig();
  const { activePrimaryFilterLength } = usePrimaryFilterState(
    primaryFilter?.id
  );
  const isAdaptiveChartsEnabled = useUnleashFlag(FeatureFlag.AdaptiveCharts);
  const baseChartType = item?.plot?.chartType;
  const isAdaptiveChartMode = Boolean(
    isAdaptiveChartsEnabled &&
      item?.plot?.adaptive &&
      baseChartType &&
      ADAPTIVE_MAP[baseChartType]
  );

  if (isAdaptiveChartMode && activePrimaryFilterLength === 1 && baseChartType) {
    return {
      chartType: ADAPTIVE_MAP[baseChartType],
      activePrimaryFilterLength,
      isAdaptiveChartMode,
    };
  }

  return {
    chartType: item?.plot?.chartType,
    activePrimaryFilterLength,
    isAdaptiveChartMode,
  };
};

const getEntityCountForAdaptiveDataSync = (
  data: DataProviderSet
): number | undefined => {
  if (
    data.type === DataType.SINGLE ||
    data.type === DataType.BREAKDOWN_SINGLE
  ) {
    return data.data.length;
  }
  return undefined;
};

export const shouldMaskAdaptiveEntityTransition = ({
  isAdaptiveChartMode,
  baseChartType,
  activePrimaryFilterLength,
  data,
}: {
  isAdaptiveChartMode: boolean;
  baseChartType: ChartType;
  activePrimaryFilterLength: number;
  data: DataProviderSet;
}): boolean => {
  if (!isAdaptiveChartMode || activePrimaryFilterLength < 1) {
    return false;
  }
  if (!ADAPTIVE_MAP[baseChartType]) {
    return false;
  }
  const entityCount = getEntityCountForAdaptiveDataSync(data);
  if (entityCount === undefined || entityCount < 1) {
    return false;
  }
  return entityCount !== activePrimaryFilterLength;
};
